/**
 * Cliente HTTP do frontend. REGRA: só fala com `/api/*` (BFF local). Nenhuma
 * URL externa, chave ou token aqui.
 */
const API_BASE = '/api/demo';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  });

  if (!response.ok) {
    let message = `Falha na requisição (${response.status})`;
    let code: string | null = null;
    try {
      const body = (await response.json()) as { error?: unknown; code?: unknown; detail?: unknown };
      if (typeof body.error === 'string') message = body.error;
      if (typeof body.code === 'string') code = body.code;
    } catch {
      // corpo não-JSON
    }
    throw new ApiError(message, response.status, code);
  }

  return response.json() as Promise<T>;
}

export type DemoConfig = { identityPoolId: string; region: string; mockLiveness: boolean };

export type DemoPerson = {
  id: string;
  fullName: string;
  documentMasked: string;
  enrolledAt: string | null;
};

export type SelfieResponse = {
  ok: true;
  confidence: number;
  status: string;
  indexJobId: string | null;
};

export type IndexStatusResponse = {
  status: 'pending' | 'processing' | 'done' | 'error' | 'unknown';
  error: string | null;
};

export type RecognizeResponse =
  | { matched: true; person: DemoPerson; similarity: number }
  | { matched: false; reason: 'NO_MATCH' | 'NO_FACE' };

export type CompletePayload = {
  enrollmentId: string;
  fullName: string;
  email: string;
  birthDate: string;
  nationality: string;
  document: string;
};

export const api = {
  config: () => request<DemoConfig>('/config'),

  enroll: {
    start: () =>
      request<{ enrollmentId: string }>('/enroll/start', {
        method: 'POST',
        body: JSON.stringify({ consentAccepted: true }),
      }),
    livenessSession: (enrollmentId: string) =>
      request<{ livenessSessionId: string }>('/enroll/liveness-session', {
        method: 'POST',
        body: JSON.stringify({ enrollmentId }),
      }),
    selfie: (payload: { enrollmentId: string; livenessSessionId?: string; base64Image?: string }) =>
      request<SelfieResponse>('/enroll/selfie', { method: 'POST', body: JSON.stringify(payload) }),
    indexStatus: (jobId: string) =>
      request<IndexStatusResponse>(`/enroll/index-status/${encodeURIComponent(jobId)}`),
    complete: (payload: CompletePayload) =>
      request<{ ok: true; person: DemoPerson }>('/enroll/complete', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  },

  recognize: (base64Image: string) =>
    request<RecognizeResponse>('/recognize', { method: 'POST', body: JSON.stringify({ base64Image }) }),
};
